import { useState } from "react";

import type { OverviewItem, OverviewJobCounts } from "@/api/import/overview";
import OverviewCard, { OverviewCardEmpty, OverviewCardRow } from "@/pages/import/OverviewPage/OverviewCard";

type JobOutcome = "successes" | "errors";

type JobsCardProps = {
  title: string;
  counts: OverviewJobCounts;
  activityPath: string;
  emptyMessage: string;
};

const outcomeCount = (item: OverviewItem, outcome: JobOutcome) =>
  outcome === "successes" ? item.success_count : item.error_count;

const JobsCard = ({ title, counts, activityPath, emptyMessage }: JobsCardProps) => {
  const [outcome, setOutcome] = useState<JobOutcome>("errors");

  const toggle = (
    <div className="flex items-center gap-1 rounded-md bg-stone-100 p-0.5">
      {(["successes", "errors"] as const).map((option) => {
        const isActive = outcome === option;
        const count = option === "successes" ? counts.success_count : counts.error_count;

        return (
          <button
            key={option}
            type="button"
            aria-pressed={isActive}
            onClick={() => setOutcome(option)}
            className={
              isActive
                ? "rounded-sm bg-white px-2 py-0.5 text-xs text-stone-800 capitalize shadow-sm"
                : "rounded-sm px-2 py-0.5 text-xs text-stone-500 capitalize"
            }
          >
            {option}{" "}
            <span className={option === "errors" && count > 0 ? "font-medium text-red-600" : "font-medium"}>
              {count}
            </span>
          </button>
        );
      })}
    </div>
  );

  return (
    <OverviewCard title={title} to={activityPath} linkLabel={`View ${title} in Activity`} headerAction={toggle}>
      {counts[outcome].length === 0 ? (
        <OverviewCardEmpty message={emptyMessage} />
      ) : (
        counts[outcome].map((item) => (
          <OverviewCardRow
            key={item.id}
            name={item.name}
            providerName={item.provider_name}
            value={
              <span className={outcome === "errors" ? "text-red-600" : undefined}>{outcomeCount(item, outcome)}</span>
            }
          />
        ))
      )}
    </OverviewCard>
  );
};

export default JobsCard;
